import { useCallback, useMemo, useRef, useState, type FC } from 'react'
import Tooltip from '@mui/material/Tooltip'
import type { GridColumn, GridSortDirection } from './types'
import { toHeaderCase } from './headerCase'
import classes from './grid-theme.module.scss'

interface SortGlyphProps {
  direction: GridSortDirection
}

/**
 * Stroked chevrons rather than solid triangles: at header size a filled
 * triangle reads as a blob, while a chevron keeps its shape. Idle shows the
 * pair (either direction is available); a sorted column keeps only its own.
 */
const SortGlyph: FC<SortGlyphProps> = ({ direction }) => {
  const stroke = {
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.7,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  }

  if (direction === 'asc') {
    return (
      <svg width="15" height="15" viewBox="0 0 16 16" aria-hidden="true">
        <path d="M4 9.5 8 5.5l4 4" {...stroke} />
      </svg>
    )
  }
  if (direction === 'desc') {
    return (
      <svg width="15" height="15" viewBox="0 0 16 16" aria-hidden="true">
        <path d="M4 6.5 8 10.5l4-4" {...stroke} />
      </svg>
    )
  }
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" aria-hidden="true">
      <path d="M4.5 6.75 8 3.25l3.5 3.5" {...stroke} />
      <path d="M4.5 9.25 8 12.75l3.5-3.5" {...stroke} />
    </svg>
  )
}

/**
 * The design system's funnel (src/components/icons/Filter.tsx), inlined rather
 * than imported: the icon is an MUI SvgIcon, and a header renders one per
 * filterable column on every list.
 */
const FilterGlyph: FC = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M2.319 4.186A.75.75 0 0 1 3 3.75h18a.75.75 0 0 1 .57 1.238l-7.02 8.198V21a.75.75 0 0 1-1.082.672l-3.6-1.777a.75.75 0 0 1-.418-.673v-6.036L2.43 4.988a.75.75 0 0 1-.111-.802Zm2.31 1.064 6.14 7.171a.75.75 0 0 1 .181.488v5.847l2.1 1.037V12.91a.75.75 0 0 1 .18-.488l6.14-7.171H4.63Z"
      fill="currentColor"
    />
  </svg>
)

interface GridHeaderCellProps<T> {
  column: GridColumn<T>
  width: number
  sortDirection: GridSortDirection
  onToggleSort: (field: string) => void
  onResize: (field: string, width: number) => void
  pinned?: 'left' | 'right'
  offset: number
  isPinnedEdge: boolean
  /** Present only when the column declares a filter. */
  onOpenFilter?: (field: string, anchor: HTMLElement) => void
  isFilterActive?: boolean
}

const GridHeaderCell = <T,>({
  column,
  width,
  sortDirection,
  onToggleSort,
  onResize,
  pinned,
  offset,
  isPinnedEdge,
  onOpenFilter,
  isFilterActive,
}: GridHeaderCellProps<T>) => {
  const [isResizing, setIsResizing] = useState(false)
  const resizeStateRef = useRef<{ startX: number; startWidth: number } | null>(null)

  const sortable = column.sortable !== false
  const resizable = column.resizable !== false

  // Pointer events (not mouse) so a resize started with a trackpad or pen keeps
  // tracking once the pointer leaves the 6px handle.
  const handlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      event.preventDefault()
      event.stopPropagation()

      const handle = event.currentTarget
      handle.setPointerCapture(event.pointerId)
      resizeStateRef.current = { startX: event.clientX, startWidth: width }
      setIsResizing(true)

      const onMove = (moveEvent: PointerEvent) => {
        const state = resizeStateRef.current
        if (!state) return
        const delta = moveEvent.clientX - state.startX
        const min = column.minWidth ?? 60
        const max = column.maxWidth ?? Infinity
        onResize(column.field, Math.min(Math.max(state.startWidth + delta, min), max))
      }

      const onUp = () => {
        resizeStateRef.current = null
        setIsResizing(false)
        handle.releasePointerCapture?.(event.pointerId)
        handle.removeEventListener('pointermove', onMove)
        handle.removeEventListener('pointerup', onUp)
        handle.removeEventListener('pointercancel', onUp)
      }

      handle.addEventListener('pointermove', onMove)
      handle.addEventListener('pointerup', onUp)
      handle.addEventListener('pointercancel', onUp)
    },
    [column.field, column.minWidth, column.maxWidth, width, onResize],
  )

  const label = useMemo(
    () => toHeaderCase(column.headerName ?? column.field),
    [column.headerName, column.field],
  )

  // Inside the label, not painted over the cell: an asterisk is what "required"
  // looks like, and putting it in the text means it wraps with the last word
  // instead of floating in a corner the label may be nowhere near.
  const labelContent = column.required ? (
    <>
      {label}
      <span className={classes.headerRequiredMark} aria-hidden="true">
        *
      </span>
      <span className={classes.srOnly}> (required)</span>
    </>
  ) : (
    label
  )

  const pinnedClass =
    pinned === 'left'
      ? `${classes.headerPinnedLeft} ${isPinnedEdge ? classes.pinnedLeftEdge : ''}`
      : pinned === 'right'
        ? `${classes.headerPinnedRight} ${isPinnedEdge ? classes.pinnedRightEdge : ''}`
        : ''

  return (
    <div
      className={[
        classes.headerCell,
        sortable ? classes.headerSortable : '',
        column.numeric ? classes.headerNumeric : '',
        pinnedClass,
        column.headerClass ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
      style={{
        width,
        minWidth: width,
        maxWidth: width,
        ...(pinned === 'left' ? { left: offset } : {}),
        ...(pinned === 'right' ? { right: offset } : {}),
      }}
      data-col-id={column.field}
      role="columnheader"
      aria-sort={
        sortDirection === 'asc'
          ? 'ascending'
          : sortDirection === 'desc'
            ? 'descending'
            : 'none'
      }
      onClick={() => {
        if (sortable) onToggleSort(column.field)
      }}
    >
      {column.headerRenderer ? (
        <column.headerRenderer column={column} sortDirection={sortDirection} />
      ) : column.headerTooltip ? (
        <Tooltip title={column.headerTooltip} arrow placement="top" disableInteractive>
          <span className={classes.headerLabel}>{labelContent}</span>
        </Tooltip>
      ) : (
        <span className={classes.headerLabel} title={label}>
          {labelContent}
        </span>
      )}

      {sortable && (
        <span
          className={`${classes.sortIcon} ${sortDirection ? classes.sortIconActive : ''}`}
        >
          <SortGlyph direction={sortDirection} />
        </span>
      )}

      {onOpenFilter && (
        <button
          type="button"
          className={`${classes.filterButton} ${
            isFilterActive ? classes.filterButtonActive : ''
          }`}
          aria-label={`Filter ${column.headerName ?? column.field}`}
          onClick={(event) => {
            // The header itself toggles the sort.
            event.stopPropagation()
            onOpenFilter(column.field, event.currentTarget)
          }}
        >
          <FilterGlyph />
        </button>
      )}

      {resizable && (
        <div
          className={`${classes.resizeHandle} ${isResizing ? classes.resizing : ''}`}
          onPointerDown={handlePointerDown}
          onClick={(e) => e.stopPropagation()}
        />
      )}
    </div>
  )
}

export default GridHeaderCell
